import { useState, useEffect } from 'react'
import { useServices } from '../../core/services/ServicesContext'
import type { ScenarioItem } from '../../core/domain/models'
import { realtimeService } from '../../api/signalr'

export function useScenarioItems(scenarioId: string | undefined, options: { passive?: boolean } = {}) {
    const { memoryState } = useServices()
    const [items, setItems] = useState<ScenarioItem[]>([])
    // Optimization: Check memory immediately to avoid initial loading flash if data exists
    const [loading, setLoading] = useState(() => {
        if (!scenarioId) return false
        return !memoryState.isScenarioLoaded(scenarioId)
    })

    useEffect(() => {
        if (!scenarioId) {
            setItems([])
            setLoading(false)
            return
        }

        // Subscribe to real-time updates (if not passive)
        if (!options.passive) {
            realtimeService.subscribeToScenario(scenarioId)
        }

        const updateState = () => {
            const data = memoryState.getScenarioItems(scenarioId)
            setItems(data || [])
            
            if (memoryState.isScenarioLoaded(scenarioId)) {
                setLoading(false)
            }
        }

        // Initial load
        // Note: We rely on ScenarioLayout (or parent) to hydrate the scenario
        // This hook just reads state reactively
        if (!memoryState.isScenarioLoaded(scenarioId)) {
            setLoading(true)
            // Still check immediately in case it loaded while we were mounting
            updateState()
        } else {
            updateState()
            setLoading(false)
        }

        const unsubscribe = memoryState.onStateChange(scenarioId, updateState)
        
        return () => {
            unsubscribe()
            if (!options.passive) {
                realtimeService.unsubscribeFromScenario(scenarioId)
            }
        }
    }, [scenarioId, memoryState, options.passive])

    return { items, loading }
}
